/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* istanbul ignore file */
import { PayloadAction } from '@reduxjs/toolkit';
import {
    call, put, select, takeEvery, takeLatest,
} from 'redux-saga/effects';
import {
    genericBladeFetchFulfilled,
    genericBladeFetchRejected,
    genericBladeUpdateFetchUrl,
    getLoanPurposesFulfilled,
    loanPurposeAEM,
    updateLoanPurposeEndpoints,
} from '@commblades/commblades';

import {
    type ApplicationDataContent, BLADE_NAME, type BladeNameT, type RootModel,
} from './types';
import {
    completeBladeNavigation,
    handleError,
    selectBlades,
    startAppInit,
    startAppInitCompleted,
    startBladeNavigation,
    startPrevBladeNavigation,
} from './navigationSlice';
import { selectApplicationContent, selectRootModel } from '../netBank/netBankSelectors';
import { applicationStartBladeSubmitWatchSaga } from './blades/applicationStart/applicationStartSaga';
import { loanPurposesBladeSubmitWatchSaga } from './blades/loanPurposes/loanPurposesSaga';
import { subLoanPurposesBladeSubmitWatchSaga } from './blades/subLoanPurpose/subLoanPurposesSaga';
import { LoanPurposeCardRaw, LoanPurposePictogramMap } from './blades/loanPurposes/loanPurposesStates';
import { getBorrowingPowerDataFulfilled } from './blades/borrowingPower/borrowingPowerSlice';

const updateTitle = (str: string): void => {
    document.title = `BorrowingEstimator -${str.replace(/([A-Z])/g, ' $1')}`;
};

/**
 * Handles the workflow for blade navigation.
 * @param action - The payload action containing the blade name or null.
 */
function* bladeNavigationWorkFlow(action: PayloadAction<BladeNameT | null>) {
    if (action.payload) {
        // TODO: getting proper blade title
        yield call(updateTitle, action.payload);
    }

    if (action.payload === BLADE_NAME.GET_STARTED) {
        yield put(genericBladeUpdateFetchUrl({ id: 'get-started', url: '' }));
    }

    if (action.payload === BLADE_NAME.SORRY) {
        yield put(genericBladeUpdateFetchUrl({ id: 'sorry', url: '' }));
    }

    if (action.payload === BLADE_NAME.LOAN_PURPOSE) {
        yield put(updateLoanPurposeEndpoints({
            getLoanPurposeApiUrl: '',
        }));
    }
    if (action.payload === BLADE_NAME.SUB_LOAN_PURPOSE) {
        yield put(genericBladeUpdateFetchUrl({ id: 'sub-loanpurpose', url: '' }));
    }
}

/**
 * Watches for navigation actions and triggers the corresponding workflow.
 */
function* navigationFlowWatchSaga() {
    yield takeEvery([
        startBladeNavigation.type,
        startPrevBladeNavigation.type,
    ], bladeNavigationWorkFlow);
}

function* completeNavigationWorkSaga(action: PayloadAction<BladeNameT>) {
    const blades: BladeNameT[] = yield select(selectBlades);

    const currentAddedBlade: BladeNameT = action.payload;
}

function* completeNavigationWatchSaga() {
    yield takeLatest(completeBladeNavigation.type, completeNavigationWorkSaga);
}

function* genericBladeFetchRejectedWorkSaga() {
    yield put(handleError({ errorMessage: 'generic blade fetch rejected.' }));
}

function* genericBladeFetchRejectedWatchSaga() {
    yield takeEvery(genericBladeFetchRejected.type, genericBladeFetchRejectedWorkSaga);
}

function* appInitWorkSaga() {
    try {
        const rootModel: RootModel = yield select(selectRootModel);

        const applicationContent: ApplicationDataContent = yield select(selectApplicationContent);
        const {
            getStarted, loanPurposes, borrowingPower,
        } = rootModel;
        const sorry = applicationContent;
        const mappedLoanPurposes = loanPurposes.data.reduce((acc: LoanPurposeCardRaw[], { name, id, description }, idx) => [...acc, {
            id,
            description,
            name,
            pictogram: LoanPurposePictogramMap.filter((pictogram) => pictogram.loanPurposeId === id)[0].pictogram,
            pictogramTheme: 'beacon',
            title: name,
            value: `${name}-${idx}`,
        }], []);

        yield put(genericBladeFetchFulfilled({ id: 'get-started', fetchPayload: { content: getStarted.content } }));

        const content: loanPurposeAEM = JSON.parse(loanPurposes.content);

        yield put(getLoanPurposesFulfilled({
            content,
            loanPurposeData:
            {
                loanPurposes: mappedLoanPurposes,
            },
        }));

        yield put(getBorrowingPowerDataFulfilled(borrowingPower));

        yield put(genericBladeFetchFulfilled({ id: 'sorry', fetchPayload: { content: sorry.sorry.content.heading } }));

        yield put(startAppInitCompleted());
    } catch (e) {
        yield put(handleError({ errorMessage: (e as Error).message, navigateToErrorPage: false }));
    }
}

function* appInitWatchSaga() {
    yield takeEvery(startAppInit.type, appInitWorkSaga);
}

export const navigationWatchSaga = [
    appInitWatchSaga,
    navigationFlowWatchSaga,
    completeNavigationWatchSaga,
    genericBladeFetchRejectedWatchSaga,
    applicationStartBladeSubmitWatchSaga,
    loanPurposesBladeSubmitWatchSaga,
    subLoanPurposesBladeSubmitWatchSaga,
];
